import { Box, Button, Card, Typography, debounce } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import { useGroupChannelListContext } from '@sendbird/uikit-react/GroupChannelList/context';
import { useSearchParams } from 'react-router';
import NewChannelIcon from '../assets/icons/new_channel.svg?react';
import SearchInput from '../common/components/SearchInput';
import { currentContextAtom } from '../state';
import { useContractsByCompanyIdQuery } from '../queries/__generated__/contractsByCompanyId.generated';
import { RoutePaths } from '../routes/AppRoutes';
import TopAppBar from '../common/components/TopAppBar';
import ChannelListPreview from './ChannelListPreview';
import { channelListQueryParamsAtom, currentChannelAtom, newChannelSlideOpenAtom } from './state';

export default function ChannelList(): React.JSX.Element {
  const { companyId } = useAtomValue(currentContextAtom);
  const { groupChannels, loadMore } = useGroupChannelListContext();
  const { t } = useTranslation(['common', 'chat']);
  const setCurrentChannel = useSetAtom(currentChannelAtom);
  const [channelListQueryParams, setChannelListQueryParams] = useAtom(channelListQueryParamsAtom);
  const { data: contractsData } = useContractsByCompanyIdQuery({ variables: { companyId } });
  const [searchNoResult, setSearchNoResult] = useState(false);
  const [query, setQuery] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultChannelUrl = searchParams.get('channelUrl');

  const setNewChannelSlideOpen = useSetAtom(newChannelSlideOpenAtom);
  const channelListRef = useRef<HTMLInputElement | null>(null);

  const debounceQuery = useMemo(() => {
    return debounce(setQuery, 200);
  }, []);

  const selectChannel = (channel: GroupChannel): void => {
    setCurrentChannel(channel);

    if (defaultChannelUrl) {
      /*
        this is done so that when a user click on another chat then navigate to another page and click on back button of navigator or phone
        the last clicked conversation should be the one to be open, not the one set by default url
      */
      setSearchParams({});
    }
  };

  useEffect(() => {
    if (contractsData) {
      if (!query) {
        setChannelListQueryParams({
          ...channelListQueryParams,
          channelUrlsFilter: undefined,
        });
      } else {
        const channelUrls = contractsData.contract
          .filter(({ buying_company, supplying_company, channel_url }) => {
            return !!channel_url
              && (buying_company?.tradeName.toLowerCase().includes(query) || supplying_company?.tradeName.toLowerCase().includes(query));
          }).map(({ channel_url }) => channel_url) as string[];

        if (channelUrls?.length) {
          setSearchNoResult(false);
          setChannelListQueryParams({
            ...channelListQueryParams,
            channelUrlsFilter: channelUrls!,
          });
        } else {
          setSearchNoResult(true);
        }
      }
    }
  }, [query, contractsData]);

  useEffect(() => {
    if (groupChannels.length) {
      setSearchNoResult(false);
    }
  }, [groupChannels]);

  return (
    <>
      <TopAppBar
        configuration="small"
        title={t('chat:messages')}
        backTo={RoutePaths.ROOT}
        trailingAction={(
          <Button variant="text" onClick={() => setNewChannelSlideOpen(true)}>
            <NewChannelIcon />
          </Button>
        )}
        sx={{
          position: {
            xs: 'fixed',
            lg: 'none', 
          },
        }}
      />
      <Card
        className="Kheops-ChatListPage-container"
        sx={{
          height: 'calc(100% - 32px)',
          px: 2,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            height: '100%',
          }}
          ref={channelListRef}
        >
          <Box
            sx={{
              display: {
                xs: 'none',
                lg: 'flex',
              },
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 0,
                px: 1,
              }}
            >
              <Typography variant="titleLarge">{t('chat:messages')}</Typography>
              <Button variant="text" onClick={() => setNewChannelSlideOpen(true)}>
                <NewChannelIcon />
              </Button>
            </Box>
          </Box>
          <SearchInput sx={{ px: 1 }} onChange={(inputQuery) => debounceQuery(inputQuery.toLowerCase())} />
          <Box
            sx={{ overflow: 'auto' }}
            onScroll={(event) => {
              if (event.currentTarget.scrollHeight - event.currentTarget.offsetHeight === event.currentTarget.scrollTop) {
                loadMore();
              }
            }}
          >
            {!!groupChannels.length && !searchNoResult && (
              <>
                {groupChannels.map((channel) => (
                  <ChannelListPreview
                    onSelectChannel={() => selectChannel(channel)}
                    channel={channel}
                    key={channel.url}
                  />
                ))}
              </>
            )}
            {searchNoResult && (
              <Typography variant="bodyMedium" sx={{ textAlign: 'center', py: 3 }}>
                {t('common:no_result')}
              </Typography>
            )}
          </Box>
        </Box>
      </Card>
    </>
  );
}
